import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { FetchError, GetIssuesDto, IssueDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { useSignalR } from "../../../hooks";
import { optimisticallyUpdateQueryData } from "../utilities";
import { IssueStatus } from "src/constants";

type Variables = {
  issueGuid: string;
  teamSlug?: string;
  statusId: IssueStatus;
  createdByUserId: string;
};

const useEditIssueStatusRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueStatusRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/status`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({
        statusId: variables.statusId,
      }),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueStatusRequest };
};

const useEditIssueStatus = () => {
  const { editIssueStatusRequest } = useEditIssueStatusRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueStatusRequest,
    onMutate: async (variables) => {
      const cachedIssueDetailsData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => {
          if (!cachedData) return cachedData;
          return {
            ...cachedData,
            currentStatus: variables.statusId,
          };
        }
      );

      let cachedTeamIssuesData: GetIssuesDto | undefined;
      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) => {
              if (issue.guid === variables.issueGuid) {
                return {
                  ...issue,
                  currentStatus: variables.statusId,
                };
              }
              return issue;
            }),
          })
        );
      }

      const cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.map((issue) => {
            if (issue.guid === variables.issueGuid) {
              return {
                ...issue,
                currentStatus: variables.statusId,
              };
            }
            return issue;
          }),
        })
      );

      return { cachedIssueDetailsData, cachedTeamIssuesData, cachedUserIssuesData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueDetailsData);
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData
        );
      }
      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        context?.cachedUserIssuesData
      );
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }
      signalR.sendRefetchUserIssues(variables.createdByUserId);
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUserId));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditIssueStatus };
